<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Fracciones Arancelarias
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Catálogo de fracciones arancelarias
      </p>
    </div>
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Lista de Fracciones
          </h2>
          <button @click="openModal()" class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nueva Fracción
          </button>
        </div>
        <!-- Filtros -->
        <div class="flex flex-wrap gap-2 mt-4">
          <input
            v-model="filtros.buscar"
            @keyup.enter="buscarFracciones"
            type="text"
            placeholder="Buscar por código o descripción..."
            class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <select
            v-model="filtros.estatus"
            @change="buscarFracciones"
            class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">Todos los estatus</option>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </select>
          <button @click="buscarFracciones" class="px-4 py-2 rounded bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition">
            Buscar
          </button>
          <button @click="limpiarFiltros" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">
            Limpiar
          </button>
        </div>
      </div>
      <div class="p-6">
        <div v-if="error" class="text-red-500 text-center mb-4">{{ error }}</div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-4 py-2 font-bold">Código</th>
                <th class="px-4 py-2 font-bold">Descripción</th>
                <th class="px-4 py-2 font-bold">Estatus</th>
                <th class="px-4 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fraccion in fracciones" :key="fraccion.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 even:bg-gray-50 dark:even:bg-gray-700">
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">
                  {{ fraccion.codigo }}
                </td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">
                  {{ fraccion.descripcion }}
                </td>
                <td class="px-4 py-2">
                  <span
                    :class="fraccion.estatus === 'activo' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'"
                    class="px-2 py-1 text-xs font-medium rounded-full"
                  >
                    {{ fraccion.estatus === 'activo' ? 'Activo' : 'Inactivo' }}
                  </span>
                </td>
                <td class="px-4 py-2">
                  <div class="flex gap-2">
                    <button @click="openModal(fraccion)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button @click="eliminarFraccion(fraccion)" class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="fracciones.length === 0">
                <td colspan="4" class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No se encontraron fracciones
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div v-if="pagination.total > 0" class="flex justify-between items-center mt-6">
          <div class="text-sm text-gray-700 dark:text-gray-300">
            Mostrando {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} a {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} de {{ pagination.total }} fracciones
          </div>
          <div class="flex gap-2">
            <button
              @click="cambiarPagina(pagination.current_page - 1)"
              :disabled="pagination.current_page <= 1"
              class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span class="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
              Página {{ pagination.current_page }} de {{ pagination.last_page }}
            </span>
            <button
              @click="cambiarPagina(pagination.current_page + 1)"
              :disabled="pagination.current_page >= pagination.last_page"
              class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-gray-900 dark:text-white">
            {{ editingFraccion ? 'Editar Fracción' : 'Nueva Fracción' }}
          </h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="submitForm">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Código <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.codigo"
                type="text"
                required
                maxlength="50"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción <span class="text-red-500">*</span>
              </label>
              <textarea
                v-model="form.descripcion"
                required
                maxlength="200"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              ></textarea>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estatus
              </label>
              <select
                v-model="form.estatus"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-md hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50"
            >
              {{ loading ? 'Guardando...' : (editingFraccion ? 'Actualizar' : 'Crear') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useHttp } from '@/composables/useHttp.js';
import Swal from 'sweetalert2';

const { get, post, put, delete: del, data } = useHttp();

const fracciones = ref([]);
const loading = ref(false);
const error = ref('');
const showModal = ref(false);
const editingFraccion = ref(null);

const filtros = ref({
  buscar: '',
  estatus: ''
});

const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 15,
  total: 0
});

const form = ref({
  codigo: '',
  descripcion: '',
  estatus: 'activo'
});

const resetForm = () => {
  form.value = {
    codigo: '',
    descripcion: '',
    estatus: 'activo'
  };
};

const openModal = (fraccion = null) => {
  editingFraccion.value = fraccion;
  if (fraccion) {
    form.value = { ...fraccion };
  } else {
    resetForm();
  }
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingFraccion.value = null;
  resetForm();
};

const cargarFracciones = async (pagina = 1) => {
  try {
    loading.value = true;
    error.value = '';

    const params = new URLSearchParams({
      page: pagina,
      per_page: pagination.value.per_page
    });

    if (filtros.value.buscar) {
      params.append('buscar', filtros.value.buscar);
    }
    if (filtros.value.estatus) {
      params.append('estatus', filtros.value.estatus);
    }

    await get(`/fracciones?${params}`);
    fracciones.value = data.value.data;
    pagination.value = {
      current_page: data.value.current_page,
      last_page: data.value.last_page,
      per_page: data.value.per_page,
      total: data.value.total
    };
  } catch (err) {
    console.log(err);
    error.value = err.data.value?.data?.message || 'Error al cargar fracciones';
  } finally {
    loading.value = false;
  }
};

const buscarFracciones = () => {
  cargarFracciones(1);
};

const limpiarFiltros = () => {
  filtros.value = {
    buscar: '',
    estatus: ''
  };
  cargarFracciones(1);
};

const cambiarPagina = (pagina) => {
  if (pagina >= 1 && pagina <= pagination.value.last_page) {
    cargarFracciones(pagina);
  }
};

const submitForm = async () => {
  try {
    loading.value = true;

    if (editingFraccion.value) {
      await put(`/fracciones/${editingFraccion.value.id}`, form.value);
      Swal.fire('¡Éxito!', 'Fracción actualizada correctamente', 'success');
    } else {
      await post('/fracciones', form.value);
      Swal.fire('¡Éxito!', 'Fracción creada correctamente', 'success');
    }

    closeModal();
    cargarFracciones(pagination.value.current_page);
  } catch (err) {
    const message = err.response?.data?.message || 'Error al procesar la solicitud';
    Swal.fire('Error', message, 'error');
  } finally {
    loading.value = false;
  }
};

const eliminarFraccion = async (fraccion) => {
  try {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas eliminar la fracción ${fraccion.codigo}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      await del(`/fracciones/${fraccion.id}`);
      Swal.fire('¡Eliminado!', 'Fracción eliminada correctamente', 'success');
      cargarFracciones(pagination.value.current_page);
    }
  } catch (err) {
    const message = err.response?.data?.message || 'Error al eliminar fracción';
    Swal.fire('Error', message, 'error');
  }
};

onMounted(() => {
  cargarFracciones();
});
</script>
